import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Row, Column, Spacer, Text, Popover, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { round } from '@oliasoft-open-source/units';
import { DateTimePicker } from 'src/views/time-tracker/date-time-picker/date-time-picker';
import { formatTimeToUI } from 'src/views/time-tracker//utils/date/dateUtils';
import { updateTimeTrackerStart } from 'src/store/entities/campaigns/campaigns';
import translations from 'src/internationalisation/translation-map.json';
import { getTotalVariance } from 'src/views/campaigns/time-tracker/time-tracker-utils';

import type { TRootState } from 'src/store/store-types';

type TTimeTracker = PropsFromRedux & {};

const TimeTrackerHeader = ({
  campaignId,
  start,
  finish,
  totalAFE,
  totalVariance,
  updateTimeTrackerStart,
}: TTimeTracker) => {
  const { t } = useTranslation();

  const updateStart = (newStart: string) => {
    if (campaignId) {
      updateTimeTrackerStart(campaignId, newStart);
    }
  };

  return (
    <>
      <Spacer />
      <Row>
        <Column flex={false}>
          <Row spacing={0}>
            <Text bold>{t(translations.timeTracker_totalAFE)}</Text>
          </Row>
          <Row spacing={0}>
            <Text>{`${totalAFE} days`}</Text>
          </Row>
        </Column>
        <Column flex={false}>
          <Row spacing={0}>
            <Text bold>{t(translations.timeTracker_startDateAndTime)}</Text>
          </Row>
          <Row spacing={0}>
            <Text>{formatTimeToUI(start)}</Text>
            <Spacer width="var(--padding-xxs)" />

            <Popover
              content={
                <DateTimePicker
                  firstOperation={{ start }}
                  close={undefined}
                  updateTrackerStartTime={updateStart}
                  updateStartTime={() => {}}
                />
              }
              placement="top-center"
            >
              <Text link>
                <div data-testid="time-tracker-header-edit-button-link">{t(translations.edit)}</div>
              </Text>
            </Popover>
          </Row>
        </Column>
        <Column flex={false}>
          <Row spacing={0}>
            <Text bold>{t(translations.timeTracker_estimatedFinish)}</Text>
          </Row>
          <Row spacing={0}>
            <Text>{formatTimeToUI(finish)}</Text>
          </Row>
        </Column>
        <Column flex={false}>
          <Row spacing={0}>
            <div data-testid="time-tracker-header-variance-label">
              <Text bold>{t(translations.timeTracker_variance)}</Text>
            </div>
          </Row>
          <Row spacing={0}>
            <div data-testid="time-tracker-header-variance-text">
              <Text>{`${totalVariance} hours`}</Text>
            </div>
          </Row>
        </Column>
      </Row>
    </>
  );
};

const mapStateToProps = ({ entities }: TRootState) => {
  const { list, timeTracker } = entities.campaigns;
  const activeCampaign = list.find((campaign) => campaign.active);
  const { start, campaignId } = activeCampaign || {};
  const operationList = timeTracker
    .flatMap((estimate) => estimate.operations)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  const { start: firstOperationStart } = operationList.at(0) || {};
  const { finish } = operationList.at(-1) || {};
  const totalAFE = round(operationList.reduce((acc, operation) => acc + operation.afe, 0) / 24, 2);
  const totalVariance = getTotalVariance(operationList);

  return {
    campaignId,
    start: start ?? firstOperationStart,
    finish,
    totalAFE,
    totalVariance,
  };
};
const mapDispatchToProps = {
  updateTimeTrackerStart,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(TimeTrackerHeader), {
  style: { position: 'absolute', top: '50px' },
});

export { Container as TimeTrackerHeader };
